import Button from '../../components/button/Button';
import Card from '../../components/card/Card';
import Spinner from '../../components/Spinner/spinner';
import useRole from '../../hooks/useRole';
import useStatus from '../../hooks/useStatus';
import { routes } from '../../routes/routePaths';

interface IButtonBarProps {
  applicationStatus: number;
  isSaving: boolean;
  onPreviousClick: () => void;
  handleSubmit: () => void;
  isSendingApplication: boolean;
  navigate: (path: string) => void;
  setIsRejectApplication: (value: boolean) => void;
  setIsApproveApplication: (value: boolean) => void;
  onApproveClick?: () => void;
  /** Overrides the primary send CTA label (e.g. "Cerrar carpeta" for syndicated). */
  submitButtonText?: string;
  /** When true on PEN/REV, hide Accept and show Send (Cerrar carpeta) instead. */
  isSyndicatedClosePath?: boolean;
}

const ButtonWithSpinner = ({
  id,
  onClick,
  variant,
  children,
  isLoading,
}: {
  id: string;
  onClick: () => void;
  variant: 'primary' | 'secondary';
  children: string;
  isLoading: boolean;
}) => (
  <Button
    id={id}
    size='lg'
    variant={variant}
    onClick={onClick}
    fullWidth
    disabled={isLoading}
  >
    {isLoading ? <Spinner size='sm' variant='primary' /> : children}
  </Button>
);

const ActionButtonBar = ({
  applicationStatus,
  onPreviousClick,
  handleSubmit,
  isSaving,
  isSendingApplication,
  navigate,
  setIsRejectApplication,
  setIsApproveApplication,
  onApproveClick,
  submitButtonText = 'Enviar solicitud',
  isSyndicatedClosePath = false,
}: IButtonBarProps) => {
  const {
    isStatusNaN,
    isRejected,
    isPending,
    isInRevision,
    isInInstrumentation,
    isFinished,
  } = useStatus({ applicationStatus });
  const { isProducer, canTransitionOrigination } = useRole();
  const isLoading = isSaving || isSendingApplication;

  const GoBackButton = (
    <Button
      id='client-button-back'
      size='lg'
      variant='secondary'
      onClick={onPreviousClick}
      fullWidth
    >
      Volver
    </Button>
  );

  const GoToDashboardButton = (
    <Button
      id='application-to-dashboard-button'
      variant='primary'
      onClick={() => navigate(routes.dashboard)}
      fullWidth
    >
      Ir al dashboard
    </Button>
  );

  const SendApplicationButton = (
    <ButtonWithSpinner
      id='application-confirm-button'
      variant='primary'
      onClick={handleSubmit}
      isLoading={isLoading}
    >
      {submitButtonText}
    </ButtonWithSpinner>
  );

  const DeclineOrChangeButton = (
    <ButtonWithSpinner
      id='application-button-reject'
      variant='secondary'
      onClick={() => setIsRejectApplication(true)}
      isLoading={isLoading}
    >
      Declinar o solicitar cambios
    </ButtonWithSpinner>
  );

  const AcceptDocumentsButton = (
    <ButtonWithSpinner
      id='application-button-approve'
      variant='primary'
      onClick={() => (onApproveClick ? onApproveClick() : setIsApproveApplication(true))}
      isLoading={isLoading}
    >
      Aceptar documentación
    </ButtonWithSpinner>
  );

  const renderProducerButtons = () => {
    if (isPending || isInRevision || isStatusNaN) {
      return (
        <>
          <Card width='200px'>{GoBackButton}</Card>
          <Card
            display='flex'
            justifyContent='flex-end'
            gap='16px'
            width='420px'
          >
            {SendApplicationButton}
          </Card>
        </>
      );
    } else {
      return (
        <>
          <Card width='200px'>{GoBackButton}</Card>
          <Card display='flex' justifyContent='flex-end' width='210px'>
            {GoToDashboardButton}
          </Card>
        </>
      );
    }
  };

  const renderNonProducerButtons = () => (
    <>
      <Card width='200px'>{GoBackButton}</Card>
      <Card display='flex' justifyContent='flex-end' gap='16px' width='600px'>
        {(isRejected || isInInstrumentation || isFinished) &&
          GoToDashboardButton}
        {canTransitionOrigination && isStatusNaN && SendApplicationButton}
        {canTransitionOrigination &&
          !isStatusNaN &&
          !isRejected &&
          !isInInstrumentation &&
          !isFinished && (
          <Card display='flex' gap='16px' width='100%'>
            {DeclineOrChangeButton}
            {isSyndicatedClosePath
              ? (isPending || isInRevision) && SendApplicationButton
              : (isPending || isInInstrumentation || isInRevision) &&
                AcceptDocumentsButton}
          </Card>
        )}
      </Card>
    </>
  );

  return (
    <Card>
      <Card display='flex' justifyContent='space-between' margin={{ top: '4' }}>
        
        {isProducer ? renderProducerButtons() : renderNonProducerButtons()}
      </Card>
    </Card>
  );
};

export default ActionButtonBar;
